import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { PopupWrapped, ContentWrapper } from '../general_components/popup_manager/popupManager';
import { RemoveScroll } from 'react-remove-scroll';
import { createStompConnection } from '../supplementary_components/stompConnection';
import { useDriversStore } from './teamCard/truckCard/hos-context';
import { useBoardStore } from './boardStore';

const NoConnection = styled.div`
    height:300px;
    background-color:var(--bg);
    display:flex;
    justify-content:center;
    align-items:center;
    color:var(--light-gray);
    font-size:2rem;
    font-weight:800;
`

function processUpdates(updates, forInvoicePage){
    if(updates){
        // MainBoard mode: push patches into Zustand store
        if(!forInvoicePage){
            const { patchTruck, trucksById } = useBoardStore.getState();
            for(let key in updates){
                const patch = updates[key];
                if(patch && typeof patch === 'object' && !("hosStatus" in patch)){
                    // /topic/trucksUpdate historically keyed by truckNumber; store is by id.
                    let truckId = key;
                    if (!trucksById[truckId]) {
                        const match = Object.values(trucksById || {}).find(
                            (t) => t && String(t.truckNumber) === String(key)
                        );
                        truckId = match?.id != null ? String(match.id) : null;
                    }
                    if (truckId) {
                        patchTruck(truckId, patch);
                    }
                }else if(patch && typeof patch === 'object' && ("hosStatus" in patch)){
                    useDriversStore.getState().upsertDriver(key, patch);
                }
            }
            return;
        }
        // Invoice page mode: now using store too
        
        const { upsertInvoiceLoad } = useBoardStore.getState();
        for(let key in updates){
             // updates for invoices are typically load updates keyed by... 
             // If key is a truckNumber, it might contain `currentLoad` or `completedLoadData`
             
             // If the update payload matches what we expect for invoices (load updates):
             const updateData = updates[key];
             if(updateData && typeof updateData === 'object'){
                 if(updateData.currentLoad) upsertInvoiceLoad(updateData.currentLoad);
                 if(updateData.completedLoadData) upsertInvoiceLoad(updateData.completedLoadData);
             }
        }
    }
}

function processTruckUpdates (updateData, notForMainBoard){    
    const truckId = updateData.truckId != null ? String(updateData.truckId) : null;
    delete updateData.truckId;
    // MainBoard mode OR Invoices mode (since we want to update the store for both)
    const { upsertLoad, upsertInvoiceLoad, patchTruck, setTruckLoads, trucksById } = useBoardStore.getState();
    const upsert = notForMainBoard ? upsertInvoiceLoad : upsertLoad;

    if(updateData.oldLoad){
        upsertInvoiceLoad(updateData.oldLoad);
        delete updateData.oldLoad;
    }
    // Load entity updates
    if(updateData.completedLoadData){
        const completed = updateData.completedLoadData;
        upsert(completed);
        
        // Only update truck relationships if we are in Main Board mode or if the truck exists in store
        // (Invoices page might not have trucks loaded, so this safety check is important)
        if(truckId && (!notForMainBoard || trucksById[truckId])){
            try {
                const prev = trucksById?.[truckId]?.previousLoadIdsForPlannerView || [];
                const next = prev.includes(completed.id) ? prev : [...prev, completed.id];
                setTruckLoads(truckId, { completedIds: next });
                if(updateData.currentLoadHasCompleted){
                    setTruckLoads(truckId, { currentLoadId: null, upcomingIds: [] });
                }
            } catch (e) {}
        }
        
        delete updateData.currentLoadHasCompleted;
        delete updateData.completedLoadId;
        delete updateData.completedLoadData;
        delete updateData.loadStatus;
    }

    if(updateData.currentLoad){
        upsert({ ...updateData.currentLoad, full: true });
        if(truckId && (!notForMainBoard || trucksById[truckId])){
            setTruckLoads(truckId, { currentLoadId: updateData.currentLoad.id });
            checkAndAddSharedLoads(updateData.currentLoad);
        }
        delete updateData.currentLoad;
    }
    if(updateData.upcomingLoad){
        upsert(updateData.upcomingLoad);
        delete updateData.upcomingLoad;
    }
    // ETA payloads are usually partial updates tied to a loadId
    if(updateData.eta && updateData.loadId){
        const { loadsById, upsertLoad } = useBoardStore.getState();
        const existingLoad = loadsById?.[updateData.loadId];
        
        // Build trucksETA object from the update
        const trucksETA = {
            eta: updateData.eta,
            late: updateData.truckIsLate === true, // Convert to boolean
            stopNumber: updateData.stopNumber
        };
        
        // Update the load with trucksETA
        const updatedLoad = existingLoad 
            ? { ...existingLoad, trucksETA }
            : { id: updateData.loadId, trucksETA };
        
        upsertLoad(updatedLoad);
        
        // Remove ETA-related fields from updateData so they don't get processed as truck patches
        delete updateData.eta;
        delete updateData.loadId;
        delete updateData.stopNumber;
        delete updateData.truckIsLate;
    }

    // Remaining truck-level patch
    if(truckId && Object.keys(updateData).length > 0){
        // Only patch truck if we are in MainBoard mode (since Invoices page doesn't display trucks)
        if(!notForMainBoard){
            patchTruck(truckId, updateData);
        }
    }
}

function processUnassignedLoads(updateData){
    const { applyUnassignedLoadsUpdate } = useBoardStore.getState();
    if(applyUnassignedLoadsUpdate){
        applyUnassignedLoadsUpdate(updateData);
        return;
    }
}
function processBoardNotesUpdate(updateData){
    const { applyBoardNotesUpdate } = useBoardStore.getState();
    if(applyBoardNotesUpdate){
        applyBoardNotesUpdate(updateData);
        return;
    }
}

/** Match TrailerService.formatLatLng — main board stores lastLocation as "lat, lng". */
function formatTrailerLastLocation(lastLocation) {
    if (lastLocation == null) return null;
    if (typeof lastLocation === 'string') {
        const trimmed = lastLocation.trim();
        return trimmed === '' ? null : trimmed;
    }
    if (typeof lastLocation !== 'object') return null;
    const lat = lastLocation.lat;
    const lng = lastLocation.lng;
    if (lat == null || lng == null || lat === '' || lng === '') return null;
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return null;
    return latNum + ', ' + lngNum;
}

/** /topic/trailers — update potentiallyDetached, lastLocation, lastUpdated on truck.currentTrailer when assignment matches board state. */
function processTrailerTelemetry(payload) {
    if (!payload || typeof payload !== 'object') return;
    // Ignore-list updates are for Trailer Management map/sites only.
    if (payload.ignoredTrailerNumbersUpdate) return;

    const { trucksById, patchTruck } = useBoardStore.getState();

    for (const [trailerNumber, loc] of Object.entries(payload)) {
        if (!loc || typeof loc !== 'object') continue;

        const truckId = loc.currentTruck;
        const truckNumber = loc.currentTruckNumber;
        if (truckId == null || truckNumber == null || truckNumber === '') continue;

        const hasPotentiallyDetached = Object.prototype.hasOwnProperty.call(loc, 'potentiallyDetached');
        const hasLastLocation = Object.prototype.hasOwnProperty.call(loc, 'lastLocation');
        const hasLastUpdated = Object.prototype.hasOwnProperty.call(loc, 'lastUpdated');
        if (!hasPotentiallyDetached && !hasLastLocation && !hasLastUpdated) continue;

        const truckIdStr = String(truckId);
        const truck = trucksById[truckIdStr];
        if (!truck?.currentTrailer) continue;

        const attached = truck.currentTrailer;
        if (String(attached.trailerNumber) !== String(trailerNumber)) continue;
        if (String(truck.truckNumber) !== String(truckNumber)) continue;

        const next = { ...attached };
        let changed = false;

        if (hasPotentiallyDetached) {
            const potentiallyDetached = !!loc.potentiallyDetached;
            if (!!attached.potentiallyDetached !== potentiallyDetached) {
                next.potentiallyDetached = potentiallyDetached;
                changed = true;
            }
        }

        if (hasLastLocation) {
            const lastLocation = formatTrailerLastLocation(loc.lastLocation);
            if ((attached.lastLocation ?? null) !== lastLocation) {
                next.lastLocation = lastLocation;
                changed = true;
            }
        }

        if (hasLastUpdated) {
            const lastUpdated = loc.lastUpdated != null ? String(loc.lastUpdated) : null;
            if ((attached.lastUpdated ?? null) !== lastUpdated) {
                next.lastUpdated = lastUpdated;
                changed = true;
            }
        }

        if (!changed) continue;

        patchTruck(truckIdStr, {
            currentTrailer: next,
        });
    }
}

export const WebSocketController = ({forInvoicePage}) => {
    const [connected, setConnected] = useState(true);
    // const timer = useRef(null);
    
    useEffect(() => {
        const connection = createStompConnection({
            getConnectHeaders: () => ({ companyId: localStorage.getItem("currentCompanyId") ?? '' }),
            onConnectionChange: setConnected,
            onConnect: (client) => {
                const companyId = localStorage.getItem("currentCompanyId");
                // Org is derived server-side from the token at handshake and is used
                // as the (server-authenticated) topic namespace.
                const orgId = localStorage.getItem('orgId');
                client.subscribe(`/topic/updates/${orgId}/${companyId}`, function(message) {
                    processTruckUpdates(JSON.parse(message.body), forInvoicePage);
                });
                if(!forInvoicePage){
                    // trucksInitialLocation is now provided in snapshot.lastSavedUpdate and applied in boardStore hydrateFromSnapshot
                    client.subscribe(`/topic/trucksUpdate/${orgId}/${companyId}`, function(message) {
                        processUpdates(JSON.parse(message.body));
                    });
                    client.subscribe(`/topic/driversUpdate/${orgId}/${companyId}`, function(message) {
                        processUpdates(JSON.parse(message.body));
                    });
                    client.subscribe(`/topic/unassignedLoads/${orgId}/${companyId}`, function(message) {
                        processUnassignedLoads(JSON.parse(message.body));
                    });
                    client.subscribe(`/topic/boardNotesUpdate/${orgId}/${companyId}`, function(message) {
                        processBoardNotesUpdate(JSON.parse(message.body));
                    });
                    client.subscribe(`/topic/trailers/${orgId}/${companyId}`, function (message) {
                        setTimeout(() => {
                            processTrailerTelemetry(JSON.parse(message.body));
                        }, 500);
                    });
                }
            },
        });
        const startTimer = setTimeout(connection.start, 500);
        return () => {
            clearTimeout(startTimer);
            connection.stop();
        };
    }, [forInvoicePage]);
    
    return <>
        {!connected ? 
            <RemoveScroll>
                <PopupWrapped>
                    <ContentWrapper $customWidth={"500px"}>
                        <NoConnection>
                            Connecting...
                        </NoConnection>
                    </ContentWrapper>
                </PopupWrapped>
            </RemoveScroll>
        :null}
    </>
}

function checkAndAddSharedLoads(currentLoad){
    if(currentLoad.loadExecutors && Array.isArray(currentLoad.loadExecutors) && currentLoad.loadExecutors.length > 1){
        try {
            const firstExecutor = currentLoad.loadExecutors[0];
            // Only proceed if percentage is not null
            if(firstExecutor && firstExecutor.percentage != null){
                const loadId = currentLoad.id;
                const { trucksById: allTrucks, setTruckLoads } = useBoardStore.getState();
                
                // Process all executors except the last one
                for(let i = 0; i < currentLoad.loadExecutors.length - 1; i++){
                    const executor = currentLoad.loadExecutors[i];
                    
                    if(executor && executor.id != null){
                        const executorTruckId = String(executor.id);
                        const truck = allTrucks[executorTruckId];
                        
                        // If truck exists, check and update previousLoadIdsForPlannerView
                        if(truck){
                            const previousLoadIds = Array.isArray(truck.previousLoadIdsForPlannerView) 
                                ? truck.previousLoadIdsForPlannerView 
                                : [];
                            
                            // Only add if not already present
                            if(!previousLoadIds.includes(loadId)){
                                const updatedIds = [...previousLoadIds, loadId];
                                setTruckLoads(executorTruckId, { completedIds: updatedIds });
                            }
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Error processing shared load executors:', error);
        }
    }
}